import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getIdTokenResult, onIdTokenChanged } from 'firebase/auth';
import { auth } from './firebase';
import Login from './components/Login';
import { MainLayout } from './components/layout';
import {
  HomePage,
  AIToolsPage,
  CRMPage,
  CorporateToolsPage,
  HRPage,
  TrainingPage,
  SupportPage,
  SurveysPage,
  AnalyticsPage,
  AvailabilityPage,
  AdminPage,
} from './components/pages';

// Wrapper component to handle navigation within the layout
const IntranetApp = ({ user, roles }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <MainLayout
      user={user}
      currentPath={location.pathname}
      onNavigate={handleNavigate}
      roles={roles}
    >
      <Routes>
        <Route path="/" element={<HomePage user={user} onNavigate={handleNavigate} />} />
        <Route path="/herramientas-ia" element={<AIToolsPage />} />
        <Route path="/crm-ventas" element={<CRMPage user={user} />} />
        <Route path="/herramientas-corporativas" element={<CorporateToolsPage />} />
        <Route path="/recursos-humanos" element={<HRPage onNavigate={handleNavigate} />} />
        <Route
          path="/recursos-humanos/disponibilidad"
          element={<AvailabilityPage user={user} roles={roles} />}
        />
        <Route path="/formacion" element={<TrainingPage />} />
        <Route path="/soporte" element={<SupportPage />} />
        <Route path="/encuestas" element={<SurveysPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route
          path="/administracion"
          element={roles.admin ? <AdminPage /> : <Navigate to="/" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MainLayout>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState({ admin: false, hr: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (nextUser) => {
      if (nextUser && nextUser.email?.endsWith('@sonneil.com')) {
        try {
          const token = await getIdTokenResult(nextUser);
          setUser(nextUser);
          setRoles({
            admin: token.claims.admin === true,
            hr: token.claims.hr === true,
          });
        } catch (error) {
          console.error('Error loading user roles:', error);
          setUser(null);
          setRoles({ admin: false, hr: false });
        }
      } else {
        setUser(null);
        setRoles({ admin: false, hr: false });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/" /> : <Login />}
        />
        <Route
          path="/*"
          element={user ? <IntranetApp user={user} roles={roles} /> : <Navigate to="/login" />}
        />
      </Routes>
    </Router>
  );
}

export default App;
